import {Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {BaseComponent} from "../../../../BaseComponent";
import {CompareActionEnum, MetricSyncStatusEnum} from "../../../../../models/device/DeviceTemplateCompare";
import {GetDataUnitsCB} from "../../../../../ope/data-unit/GetDataUnitsCB";
import {DeviceTypeOpeService} from "../../../../../services/ope/deviceTypeOpe.service";
import {DataUnit} from "../../../../../models/data-unit/DataUnit";
import {DxDataGridComponent} from "devextreme-angular";
import {ProtocolTypeEnum} from "../../../../../enums/registry/ProtocolTypeEnum";
import {DataType} from "../../../../../models/metric/DataType";
import {WriteOnEdgeOriginTypeEnum} from "../../../../../enums/registry/WriteOnEdgeOriginTypeEnum";
import {custom} from "devextreme/ui/dialog";
import {getRowKey} from "../../../../../utils/rowKey";
import * as uuid from "uuid";
import {Wm2eOriginDTO} from "../../../../../ope/gateway-pending-config/GatewayPendingConfigPayloadDTO";
import {UserMetricInstance} from "../../../../../models/user/Metric/UserMetricInstance";
import {DataTypeGroupEnum} from "../../../../../enums/dashboard/DataTypeUnitEnum";
import {DashboardService} from "../../../../../services/dashboard/dashboard.service";
import {RowStateTracker} from "../../../../../utils/RowStateTracker";
import {focusEditRow} from "../../../../../utils/focusEditRow";

// `metrics`/outputs are typed loosely (any) since this component is shared between the
// compare popup (CompareMetricVM rows) and the device drawer (MetricInstanceDraft rows) -
// those two models don't share a strict-template-compatible shape (e.g. `id` is required
// on one, optional on the other), same tradeoff the rest of this grid-heavy subsystem
// already makes (see BaseProtocolFormComponent.groupMetric: any).
@Component({
  selector: 'app-measure-metric-table',
  templateUrl: './measure-metric-table.component.html',
  styleUrls: ['./measure-metric-table.component.css']
})
export class MeasureMetricTableComponent extends BaseComponent implements OnInit {

  @ViewChild('metricDataGrid', {static: false}) dataGrid?: DxDataGridComponent;
  @ViewChild('fileInput') fileInput!: ElementRef;

  // Owned by the parent when the group table has to read it too (group rows' dot).
  @Input() rowStateTracker = new RowStateTracker();
  // Key of the group whose metrics are shown.
  @Input() rowStateScope?: any;

  private deviceTypeOpeService: DeviceTypeOpeService = inject(DeviceTypeOpeService);
  dashboardService: DashboardService = inject(DashboardService);

  @Input() metrics: any[] = [];
  @Input() protocolType?: ProtocolTypeEnum;
  @Input() groupTransportConfiguration?: any;
  @Input() showAction: boolean = false;
  @Input() showActiveColumn: boolean = true;
  @Input() showSyncStatus: boolean = false;
  @Input() showWm2eColumns: boolean = false;
  @Input() showCsvImportExport: boolean = false;
  @Input() showDelete: boolean = false;
  @Input() editable: boolean = false;
  @Input() showToolbarActions: boolean = true;
  @Input() showDeleteRowButton: boolean = true;
  @Input() dataTypeColumnReadOnly: boolean = false;
  @Input() showRawTransportColumns: boolean = true;
  @Input() emptyMessageKey!: string;
  @Input() groupName?: string;
  @Input() titleSuffix?: string;

  @Output() addMetric = new EventEmitter<any>();
  @Output() replaceMetric = new EventEmitter<any>();
  @Output() addAllMetrics = new EventEmitter<any[]>();
  @Output() deleteMetric = new EventEmitter<any>();
  @Output() metricsChanged = new EventEmitter<void>();

  // Show metric pairing popup
  pairingMetric: boolean = false;
  selectedRowReference?: any;

  protected readonly CompareActionEnum = CompareActionEnum;
  protected readonly MetricSyncStatusEnum = MetricSyncStatusEnum;
  protected readonly WriteOnEdgeOriginTypeEnum = WriteOnEdgeOriginTypeEnum;
  protected readonly ProtocolTypeEnum = ProtocolTypeEnum;

  protected rowIsBeingEdited: boolean = false;

  dataUnitValues: DataUnit[] = [];

  private readonly csvFields: string[] = [
    'name',
    'transportConfiguration.address',
    'transportConfiguration.dataType',
    'transportConfiguration.bitPosition',
    'transportConfiguration.byteOrder',
    'divider',
    'dataUnitId',
    'decimalDigits'
  ];
  private readonly csvFieldTypes: string[] = ['string', 'float', 'string', 'float', 'string', 'float', 'dataUnit', 'float'];

  ngOnInit() {
    const cb = new GetDataUnitsCB();
    this.deviceTypeOpeService.getDataUnits(cb).then(() => {
      this.dataUnitValues = cb.o.metricInstances;
    });
  }

  get hasAddableMetrics(): boolean {
    return this.metrics.some(m => m.action === CompareActionEnum.ADD);
  }

  get allMetricsSelected(): boolean {
    return this.metrics.length > 0 && this.metrics.every(m => m.selected !== false);
  }

  onMetricSelectedChanged(metric: any, value: boolean) {
    metric.selected = value;
    this.rowStateTracker.markModified(metric._uiKey, this.rowStateScope);
    this.dataGrid?.instance.refresh();
    this.metricsChanged.emit();
  }

  getSyncIcon(status?: MetricSyncStatusEnum): string {
    switch (status) {
      case MetricSyncStatusEnum.SYNCED:
        return 'vf-ic_fluent_checkmark_circle_20_filled';
      case MetricSyncStatusEnum.CONFLICT:
        return 'vf-ic_fluent_dark_theme_20_filled';
      default:
        return 'vf-ic_fluent_circle_hint_20_filled';
    }
  }

  onAddClick($event: MouseEvent, metric: any) {
    $event.stopPropagation();
    this.addMetric.emit(metric);
  }

  onAddAllClick() {
    this.addAllMetrics.emit(this.metrics.filter(m => m.action === CompareActionEnum.ADD));
  }

  onDeleteClick($event: MouseEvent, metric: any) {
    $event.stopPropagation();
    this.deleteMetric.emit(metric);
  }

  onReplaceClick($event: MouseEvent, metric: any) {
    $event.stopPropagation();
    custom({
      title: this.translate.instant('REGISTRY.DEVICE.TEMPLATE_IMPORT.CONFIRM_REPLACE_TITLE'),
      messageHtml: this.translate.instant('REGISTRY.DEVICE.TEMPLATE_IMPORT.CONFIRM_REPLACE_MESSAGE', {metricName: metric.name}),
      buttons: [
        {
          text: this.translate.instant('REGISTRY.DEVICE.TEMPLATE_IMPORT.REPLACE'),
          icon: 'refresh',
          stylingMode: 'contained',
          type: 'danger',
          onClick: () => true
        },
        {
          text: this.translate.instant('BUTTONS.CANCEL'),
          icon: 'revert',
          type: 'default',
          onClick: () => false
        }
      ]
    }).show().then((confirmed: boolean) => {
      if (confirmed)
        this.replaceMetric.emit(metric);
    });
  }

  onOptionChanged() {
    this.metricsChanged.emit();
  }

  // Called by the parent before a device save, if a row is still "in modifica" here.
  cancelActiveEdit(): void {
    this.dataGrid?.instance.cancelEditData();
  }

  focusActiveEdit(): void {
    focusEditRow(this.dataGrid);
  }

  refresh(): void {
    this.dataGrid?.instance.refresh();
  }

  async onEditingStart($event: any) {
    if (this.rowIsBeingEdited) {
      $event.cancel = true;
      const discard = await this.showDiscardEditsDialogWithPromise();
      if (discard) {
        this.rowIsBeingEdited = false;
        $event.component.editRow($event.component.getRowIndexByKey(getRowKey($event.data)));
      }
      return;
    }
    this.rowIsBeingEdited = true;
    $event.component.clearSelection();
  }

  onEditCanceled() {
    this.rowIsBeingEdited = false;
  }

  // Blocks the row save directly in the grid - a SYSTEM-origin metric with no paired
  // wm2eOriginId can't be confirmed, no need to bubble validity up to the device form.
  //
  // Pairing (see metricsSelected below) writes wm2eOriginId directly onto the live row
  // object in `metrics` rather than through DevExtreme's own setValue/cellValue APIs -
  // both proved unreliable here (their closures/column lookups silently no-op depending on
  // grid repaint timing while the pairing popup is open). e.oldData/e.newData only reflect
  // DevExtreme-tracked edits, so read the actual live row instead.
  onRowValidatingMetric(e: any) {
    const liveData = this.metrics.find(m => m._uiKey === e.key) ?? e.oldData;
    const merged = {...liveData, ...e.newData};
    const invalid = merged.wm2eOriginType === WriteOnEdgeOriginTypeEnum.SYSTEM && !merged.wm2eOriginId;
    if (invalid) {
      e.isValid = false;
      e.errorText = this.translate.instant('REGISTRY.DEVICE.METRIC.ORIGIN_METRIC_REQUIRED');
    }
    if (liveData) liveData.showInvalidBadge = invalid;
  }

  onRowSaved() {
    this.rowIsBeingEdited = false;
    this.metricsChanged.emit();
  }

  onRowRemoved() {
    this.metricsChanged.emit();
  }

  onCellPreparedSwapSaveIcon(e: any) {
    if (e.rowType === 'data' && e.column.command === 'edit') {
      const saveButton = e.cellElement.querySelector('.dx-link-save');
      if (saveButton) {
        saveButton.classList.remove('dx-icon-save');
        saveButton.classList.add('dx-icon-check');
      }
    }
  }

  private getDataTypeValues(): DataType[] {
    return this.protocolType === ProtocolTypeEnum.SIEMENS_S7
      ? this.gmService.s7DataTypeValues
      : this.gmService.modbusDataTypeValues;
  }

  getFilteredDataTypeValues(): DataType[] {
    const dataTypeValues = this.getDataTypeValues();
    if (this.groupTransportConfiguration?.function) {
      const functionValues = this.protocolType === ProtocolTypeEnum.SIEMENS_S7
        ? this.gmService.s7FunctionValues
        : this.gmService.modbusFunctionValues;
      const blacklist: string[] = functionValues.find((x: any) => x.value === this.groupTransportConfiguration.function)?.typeBlackList;
      if (Array.isArray(blacklist) && blacklist.length > 0)
        return dataTypeValues.filter((x: any) => !blacklist.includes(x.value));
    }
    return dataTypeValues;
  }

  private getFullDataType(dataType?: string): DataType | undefined {
    return this.getDataTypeValues().find(d => d.value === dataType);
  }

  private applyFullDataTypeConstraint(e: any, supported: boolean, resetValue: any) {
    e.editorOptions.disabled = !supported;
    e.editorOptions.placeholder = supported ? '' : this.translate.instant("COMMON.NOT_AVAILABLE");
    console.log("applyFullDataTypeConstraint", e.dataField, supported, resetValue, e.value);
    if (!supported && e.value !== resetValue) {
      e.setValue(resetValue);
      e.component.refresh();
    }
  }

  getMaxBit(row: any): number {
    return row?.transportConfiguration?.dataType === 'BIT32' ? 31 : 15;
  }

  validateBit = (validationRequest: any) => {
    if (validationRequest?.data?.transportConfiguration?.dataType &&
      this.getFullDataType(validationRequest.data.transportConfiguration.dataType)?.supportsBitPosition) {
      if (validationRequest.value === null || validationRequest.value === undefined)
        return false;
      const max = this.getMaxBit(validationRequest.data);
      return validationRequest.value >= 0 && validationRequest.value <= max;
    }
    return true;
  };

  validateDivider = (validationRequest: any) => {
    if (validationRequest?.data?.transportConfiguration?.dataType &&
      this.getFullDataType(validationRequest.data.transportConfiguration.dataType)?.supportsDivisor) {
      return validationRequest.value !== null && validationRequest.value !== undefined;
    }
    return true;
  };

  dividerRange = (validationRequest: any) => {
    return validationRequest.value != 0;
  };

  // Same reactive-repaint trick as setCellValueDataType: mutating newData here is what makes
  // DevExtreme re-render the wm2eOriginId editCellTemplate (which reads wm2eOriginType off the
  // row data) as soon as the origin type changes, instead of only after the row is confirmed.
  setCellValueWm2eOriginType = (newData: any, value: any) => {
    newData.wm2eOriginType = value;
    if (value !== WriteOnEdgeOriginTypeEnum.SYSTEM) {
      newData.wm2eOriginId = null;
      newData.wm2eOrigin = null;
    }
  };

  // Dependent-field reset for dataType change, following the same [setCellValue] pattern
  // already used by modbus-metric-table/s7-metric-table for this column: mutating newData
  // here is what makes DevExtreme reprepare the bitPosition/dataUnitId/divider/decimalDigits
  // editors below with the new dataType, without a manual onValueChanged hijack or full-grid refresh.
  setCellValueDataType = (newData: any, value: any) => {
    newData.transportConfiguration = {...newData.transportConfiguration, dataType: value};
    const fullDataType = this.getFullDataType(value);
    if (!fullDataType?.supportsBitPosition)
      newData.transportConfiguration.bitPosition = null;
    newData.divider = null;
    if (!fullDataType?.supportsUnit)
      newData.dataUnitId = undefined;
    if (!fullDataType?.supportsDecimals)
      newData.decimalDigits = undefined;
  };

  onEditorPreparingMetric(e: any) {
    if (e.parentType !== 'dataRow')
      return;

    if (e.row?.data?.transportConfiguration?.dataType) {
      const fullDataType = this.getFullDataType(e.row.data.transportConfiguration.dataType);

      if (e.dataField === 'transportConfiguration.bitPosition') {
        this.applyFullDataTypeConstraint(e, !!fullDataType?.supportsBitPosition, null);
      }

      if (e.dataField === 'dataUnitId') {
        this.applyFullDataTypeConstraint(e, !!fullDataType?.supportsUnit, null);
      }

      if (e.dataField === 'decimalDigits') {
        this.applyFullDataTypeConstraint(e, !!fullDataType?.supportsDecimals, null);
      }

      if (e.dataField === 'divider') {
        this.applyFullDataTypeConstraint(e, !!fullDataType?.supportsDivisor, null);
      }
    }
  }

  triggerFileSelection(): void {
    this.fileInput.nativeElement.click();
  }

  objectsToCSV(data: any[], filename: string = 'data.csv') {
    const captions = this.csvFields.map(f => this.captionForField(f));
    const csvRows: string[] = [captions.join(';')];

    for (const row of data) {
      const csvRow = this.csvFields.map(field => {
        const value = field === 'dataUnitId'
          ? (this.dataUnitValues.find(d => d.id === row.dataUnitId)?.name ?? '')
          : this.getNestedValue(row, field);
        return String(value ?? '').replace(/"/g, '""');
      });
      csvRows.push(csvRow.join(';'));
    }

    const blob = new Blob([csvRows.join('\n')], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private captionForField(field: string): string {
    const key: Record<string, string> = {
      'name': 'NAME',
      'transportConfiguration.address': 'ADDRESS',
      'transportConfiguration.dataType': 'DATA_TYPE',
      'transportConfiguration.bitPosition': 'BIT_POSITION',
      'transportConfiguration.byteOrder': 'BYTE_ORDER',
      'divider': 'DIVIDER',
      'dataUnitId': 'DATA_UNIT',
      'decimalDigits': 'DECIMAL_DIGITS'
    };
    return this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.' + key[field]);
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc && acc[key], obj);
  }

  onFileSelected(event: any): void {
    const file = event.srcElement.files[0];
    if (file) {
      try {
        this.parseCSV(file);
      } catch (e: any) {
        this.toastService.openErrorToastMessage(e);
        this.logger.error(e);
      }
    }
    event.target.value = '';
  }

  private parseCSV(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const csvData = e.target.result;
      if (!csvData) {
        this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.FILE_EMPTY'));
        return;
      }
      const csvRows: string[] = csvData.split('\n').slice(1);
      const parsedRows: any[] = [];

      for (const row of csvRows) {
        if (!row.trim())
          continue;
        const cells = row.split(';');
        let current: any = {};

        if (cells.length !== this.csvFields.length) {
          this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.INVALID_CSV_LENGTH', [csvRows.indexOf(row)]));
          return;
        }

        for (let index = 0; index < cells.length; index++) {
          const fieldPath = this.csvFields[index];
          const trimmed = cells[index].trim();
          const fieldType = this.csvFieldTypes[index];

          if (fieldType === 'string') {
            current = this.extractValue(fieldPath.split('.'), current, trimmed);
          } else if (fieldType === 'float') {
            let num: any = trimmed === '' ? null : parseFloat(trimmed);
            if (num !== null && isNaN(num)) {
              this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.INVALID_CSV', [csvRows.indexOf(row)]));
              return;
            }
            current = this.extractValue(fieldPath.split('.'), current, num);
          } else if (fieldType === 'dataUnit') {
            const selectedDataUnit = this.dataUnitValues.find(d => d.name === trimmed);
            if (selectedDataUnit) {
              current['dataUnitId'] = selectedDataUnit.id;
            } else if (!!trimmed) {
              this.toastService.openInfoToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.UNIT_DISCARDED', [csvRows.indexOf(row) + 2]));
            }
          }
        }
        parsedRows.push(current);
      }
      this.mergeCsvRows(parsedRows);
      this.cdr.detectChanges();
    };
    reader.readAsText(file);
  }

  private extractValue(keys: string[], current: any, cell: any) {
    const clone = {...current};
    let pointer = clone;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (i === keys.length - 1) {
        pointer[key] = cell;
      } else {
        pointer[key] = {...(pointer[key] || {})};
        pointer = pointer[key];
      }
    }
    return clone;
  }

  private validateCsvRow(row: any): string | null {
    const transportConfig = row.transportConfiguration ?? {};
    if (!row.name)
      return 'NAME';
    if (transportConfig.address === undefined || transportConfig.address === null
      || transportConfig.address < 0 || transportConfig.address > 65535)
      return 'ADDRESS';
    if (!this.getDataTypeValues().find(x => x.value === transportConfig.dataType))
      return 'DATA_TYPE';
    const fullDataType = this.getFullDataType(transportConfig.dataType);
    if (fullDataType?.supportsBitPosition) {
      const maxBitPosition = transportConfig.dataType === 'BIT32' ? 31 : 15;
      if (transportConfig.bitPosition === undefined || transportConfig.bitPosition === null
        || transportConfig.bitPosition < 0 || transportConfig.bitPosition > maxBitPosition)
        return 'BIT_POSITION';
    }
    if (fullDataType?.supportsDivisor
      && (row.divider === undefined || row.divider === null || row.divider < 0.000001)) {
      return 'DIVIDER';
    }
    if (!this.gmService.byteOrderValues.find((x: any) => x.value === transportConfig.byteOrder))
      return 'BYTE_ORDER';
    if (row.decimalDigits !== undefined && (row.decimalDigits < this.gmService.DECIMAL_DIGITS_MIN || row.decimalDigits > this.gmService.DECIMAL_DIGITS_MAX))
      return 'DECIMAL_DIGITS';
    return null;
  }

  // metricId is optional on device-instance rows - a CSV row matching an existing metric by
  // name edits it in place (preserving its metricId link), otherwise a new metric-less row is
  // appended, same as adding one by hand via customMetricAddRow.
  private applyDataTypeConstraints(metric: any) {
    const fullDataType = this.getFullDataType(metric.transportConfiguration.dataType);
    if (!fullDataType?.supportsDivisor)
      metric.divider = null;
    if (!fullDataType?.supportsUnit)
      metric.dataUnitId = undefined;
    if (!fullDataType?.supportsDecimals)
      metric.decimalDigits = undefined;
  }

  private mergeCsvRows(parsedRows: any[]) {
    this.cancelActiveEdit();
    // All or nothing: an invalid row must not leave the rows before it already applied
    for (let index = 0; index < parsedRows.length; index++) {
      const error = this.validateCsvRow(parsedRows[index]);
      if (error) {
        this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.INVALID_CSV_COL', [index + 2, this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.' + error)]));
        return;
      }
    }

    for (const parsed of parsedRows) {
      const target = this.metrics.find(m => m.name === parsed.name);
      if (!target) {
        const newMetric: any = {
          name: parsed.name,
          selected: true,
          transportConfiguration: {...parsed.transportConfiguration},
          divider: parsed.divider,
          decimalDigits: parsed.decimalDigits,
          dataUnitId: parsed.dataUnitId,
          _uiKey: uuid.v4()
        };
        this.applyDataTypeConstraints(newMetric);
        this.metrics.push(newMetric);
        this.rowStateTracker.markNew(newMetric._uiKey, this.rowStateScope);
        continue;
      }
      const before = this.csvSnapshot(target);
      target.transportConfiguration = {...target.transportConfiguration, ...parsed.transportConfiguration};
      target.divider = parsed.divider;
      target.decimalDigits = parsed.decimalDigits;
      if (parsed.dataUnitId !== undefined)
        target.dataUnitId = parsed.dataUnitId;
      this.applyDataTypeConstraints(target);
      if (this.csvSnapshot(target) !== before)
        this.rowStateTracker.markModified(target._uiKey, this.rowStateScope);
    }

    this.dataGrid?.instance.refresh();
    this.metricsChanged.emit();
  }

  // An empty CSV cell parses to null where the row may hold undefined, so both count as equal
  private csvSnapshot(metric: any): string {
    return JSON.stringify(this.csvFields.map(field => this.getNestedValue(metric, field) ?? null));
  }

  // A device-only new row has no template metric to link to (metricId stays null) - gets a
  // client-side _uiKey immediately since keyExpr requires one to be defined.
  onInitNewRow($event: any) {
    const defaultDataType = this.getFilteredDataTypeValues()[0]?.value;
    const fullDataType = this.getFullDataType(defaultDataType);
    $event.data.selected = true;
    $event.data.transportConfiguration = {byteOrder: this.gmService.defaultByteOrderValue?.value, dataType: defaultDataType};
    $event.data.divider = fullDataType?.supportsDivisor ? 1 : null;
    $event.data.decimalDigits = fullDataType?.supportsDecimals ? 2 : undefined;
    $event.data._uiKey = uuid.v4();
  }

  editMetricAddRowButton($event: any) {
    const toolbarItems = $event.toolbarOptions.items;
    toolbarItems.forEach((item: any) => {
      if (item.name === 'addRowButton') {
        item.options = {
          icon: 'add',
          onClick: () => this.customMetricAddRow($event)
        };
      }
    });
  }

  customMetricAddRow($event: any) {
    if (this.getFilteredDataTypeValues().length === 0) {
      this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.NO_AVAILABLE_DATA_TYPE'));
      return;
    }
    if (!this.rowIsBeingEdited) {
      $event.component.addRow().done(() => {
        this.rowIsBeingEdited = true;
        $event.component.clearSelection();
      });
    } else {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          $event.component.addRow().done(() => {
            this.rowIsBeingEdited = true;
            $event.component.clearSelection();
          });
        }
      });
    }
  }

  getSelectedMetricLabel(selectedMetric: Wm2eOriginDTO) {
    if (selectedMetric) {
      if (selectedMetric.deviceName)
        return selectedMetric.partitionName + " > " + selectedMetric.deviceName + " > " + selectedMetric.name;
      else
        return selectedMetric.partitionName + " > " + this.translate.instant("REGISTRY.DEVICE.VIRTUAL_METRICS") + " > " + selectedMetric.name;
    }
    return "---";
  }

  // Writes directly onto the live row object (the same one bound as `metrics`, matched by
  // its stable _uiKey) instead of through DevExtreme's setValue/cellValue - both go through
  // a row/column snapshot that can go stale or miss non-column fields (wm2eOrigin isn't a
  // grid column) depending on repaint timing while this popup is open. refresh() then forces
  // the grid to re-render straight from that live array.
  metricsSelected(selectedMetrics: UserMetricInstance[]) {
    this.pairingMetric = false;
    const selectedMetric = selectedMetrics[0];
    if (!selectedMetric || !this.selectedRowReference?.data)
      return;

    const liveMetric = this.metrics.find(m => m._uiKey === this.selectedRowReference.data._uiKey) ?? this.selectedRowReference.data;
    liveMetric.wm2eOriginId = selectedMetric.id;
    liveMetric.wm2eOrigin = {
      id: selectedMetric.id,
      name: selectedMetric.name,
      partitionId: selectedMetric.partitionId,
      partitionName: selectedMetric.partitionName,
      deviceId: selectedMetric.deviceId,
      deviceName: selectedMetric.deviceName,
    };
    liveMetric.showInvalidBadge = false;

    this.dataGrid?.instance.refresh();
    this.metricsChanged.emit();
  }

  getSelectableDataTypeGroups() {
    if (this.selectedRowReference?.data?.dataTypeGroup)
      return Object.values(DataTypeGroupEnum).filter(type => type !== this.selectedRowReference.data.dataTypeGroup);
    return [];
  }
}
